import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { scheduleService, ScheduleUpdate } from '../services/scheduleService';
import postService from '../services/postService';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment-timezone';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import '../styles/calendar-dark-mode.css';
import ContentQueue from '../components/schedule/ContentQueue';
import BestTimeToPost from '../components/schedule/BestTimeToPost';
import PostPreviewModal from '../components/schedule/PostPreviewModal';
import { Post } from '../services/postService';
import { useDragStore } from '../stores/dragStore';
import PostSelectorModal from '../components/schedule/PostSelectorModal';
import SchedulePostModal from '../components/schedule/SchedulePostModal';
import EventComponent from '../components/schedule/EventComponent';
import CustomCalendarToolbar from '../components/schedule/CustomCalendarToolbar';
import { Button } from '../components/ui/button';
import FloatingLabelSelect from '../components/ui/FloatingLabelSelect';
import { SelectItem } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Loader2, Calendar as CalendarIcon, Clock, Globe, Mail, AlertTriangle } from 'lucide-react';
import ComingSoonWrapper from '../components/launch/ComingSoonWrapper';
import { useScheduledQueueLimit } from '../hooks/useSocialMediaLimits';
import { Crown } from 'lucide-react';
import { UpgradeModal } from '../components/modals/UpgradeModal';

const localizer = momentLocalizer(moment);
const timezones = moment.tz.names();
const DraggableCalendar = withDragAndDrop(Calendar);

interface SchedulePageProps {}

const SchedulePage: React.FC<SchedulePageProps> = () => {
  const { client: currentClient, user } = useAuth();
  const queryClient = useQueryClient();
  const [calendarEvents, setCalendarEvents] = useState<any[]>([]);
  const [isPostSelectorOpen, setIsPostSelectorOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [timezone, setTimezone] = useState(moment.tz.guess());
  const draggedPost = useDragStore((state) => state.draggedPost);
  const { toast } = useToast();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Get queue limit info
  const queueLimit = useScheduledQueueLimit();
  
  // Check if scheduling feature is locked (limit is 0)
  const isScheduleFeatureLocked = !queueLimit.isLoading && queueLimit.limit === 0;

  // Fetch all scheduled posts (both individual and from groups)
  const { data: allScheduledPosts, isLoading: isLoadingPosts, error: postsError } = useQuery({
    queryKey: ['allScheduledPosts', currentClient?.id],
    queryFn: async () => {
      console.log('📅 Fetching all scheduled posts for client:', currentClient?.id);
      try {
        const result = await postService.getPosts(1, 100, 'scheduled'); // Max 100 per page
        console.log('✅ All scheduled posts fetched:', result?.posts?.length || 0, result?.posts);
        return result?.posts || [];
      } catch (error) {
        console.error('❌ Error fetching scheduled posts:', error);
        return [];
      }
    },
    enabled: !!currentClient,
    staleTime: 30000, // 30 seconds - reduce frequent refetches
    cacheTime: 300000, // 5 minutes
    retry: false,
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch on component mount if data exists
  });

  const calculatedEvents = useMemo(() => {
    console.log('📅 Calculating calendar events...');
    console.log('📅 allScheduledPosts:', allScheduledPosts);
    
    const allEvents = [];

    // Process all scheduled posts
    if (allScheduledPosts && allScheduledPosts.length > 0) {
      console.log(`📅 Processing ${allScheduledPosts.length} scheduled posts`);
      allScheduledPosts.forEach(post => {
        console.log(`📅 Post ${post.id}: title="${post.title}", status=${post.status}, scheduled_at=${post.scheduled_at}`);
        
        // Only add posts that have a scheduled_at date
        if (post.scheduled_at) {
          try {
            const startDate = moment.tz(post.scheduled_at, 'UTC').tz(timezone).toDate();
            allEvents.push({
              title: post.title || 'Untitled Post',
              start: startDate,
              end: startDate,
              allDay: false,
              resource: post,
            });
            console.log(`✅ Added post to calendar: ${post.title} at ${startDate}`);
          } catch (error) {
            console.error(`❌ Error parsing date for post ${post.id}:`, error);
          }
        } else {
          console.log(`⏭️ Skipped post: ${post.title} (no scheduled_at date)`);
        }
      });
    } else {
      console.log('📅 No scheduled posts found');
    }

    console.log(`📅 Total calendar events: ${allEvents.length}`);
    return allEvents;
  }, [allScheduledPosts, timezone]);

  useEffect(() => {
    // Only update calendar events if we have fresh data
    console.log('📅 Calendar events updated:', {
      eventsCount: calculatedEvents.length,
      isLoadingPosts,
      hasPostsError: !!postsError
    });
    setCalendarEvents(calculatedEvents);
  }, [calculatedEvents, isLoadingPosts, postsError]);

  const handlePostUnscheduled = (postId: string) => {
    // Immediately remove from local state for instant UI feedback
    setCalendarEvents(prevEvents => prevEvents.filter(event => event.resource.id !== postId));
    
    // Invalidate queries to refetch fresh data - this will trigger a recalculation of calculatedEvents
    // which will automatically update calendarEvents through the useEffect
    queryClient.invalidateQueries({ queryKey: ['allScheduledPosts', currentClient?.id] });
    queryClient.invalidateQueries({ queryKey: ['draftPosts'] });
    
    // Force refetch immediately instead of waiting for stale time
    queryClient.refetchQueries({ queryKey: ['allScheduledPosts', currentClient?.id] });
    
    // Note: Toast notification is handled by the component that calls this function
    // (e.g., PostPreviewModal, PostGroupPreviewModal, etc.)
  };

  const updateScheduleMutation = useMutation({
    mutationFn: ({ postId, data }: { postId: string; data: ScheduleUpdate }) =>
      scheduleService.updateScheduledPost(postId, data, currentClient!.id),
    onSuccess: () => {
      toast({
        title: "Post updated successfully!",
      });
      queryClient.invalidateQueries({ queryKey: ['allScheduledPosts', currentClient?.id] });
      queryClient.invalidateQueries({ queryKey: ['draftPosts'] });
    },
    onError: () => {
      toast({
        title: "Failed to update post.",
        variant: "destructive",
      });
    },
  });

  const handleEventDrop = useCallback(
    ({ event, start }: any) => {
      const { resource } = event;
      const updatedData: ScheduleUpdate = {
        scheduled_at: moment(start).utc().toISOString(),
        status: 'scheduled',
      };
      updateScheduleMutation.mutate({ postId: resource.id, data: updatedData });
    },
    [updateScheduleMutation]
  );

  const handleSelectEvent = (event: any) => {
    setSelectedPost(event.resource);
    setIsPreviewModalOpen(true);
  };

  const handlePostSelect = (post: Post) => {
    setSelectedPost(post);
    setIsPostSelectorOpen(false);
    setIsScheduleModalOpen(true);
  };

  // Don't block the calendar from showing if there are errors - just log them
  React.useEffect(() => {
    if (postsError) {
      console.error('⚠️ Posts query error (non-blocking):', postsError);
    }
  }, [postsError]);



  return (
    <ComingSoonWrapper
      feature="scheduling"
      title="Upgrade to Unlock Scheduling"
      description="Schedule posts with our visual calendar interface. Upgrade to a higher tier to access this feature."
      features={[
        "Drag & drop scheduling",
        "Best time recommendations",
        "Bulk scheduling tools"
      ]}
      className="schedule-page-modal"
    >
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white p-4 sm:p-6 lg:p-8 relative">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 lg:gap-0 mb-8 lg:mb-10">
        <div className="flex-1">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 100%)' }}>
              <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black bg-gradient-to-r from-gray-900 via-indigo-500 to-purple-500 bg-clip-text text-transparent dark:from-white dark:via-indigo-200 dark:to-purple-200">
                Content Calendar
              </h1>
              <p className="text-slate-600 dark:text-slate-300 text-lg mt-1">Schedule and manage your social media content</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 w-full lg:w-auto">
          {/* Calendar Stats */}
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="relative overflow-hidden rounded-xl bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200/50 dark:border-slate-600/30 px-4 py-3 shadow-sm">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/50 to-blue-50/50 dark:from-cyan-900/10 dark:to-blue-900/10"></div>
              <div className="relative text-center">
                <div className="text-2xl font-bold" style={{ color: '#30b3dd' }}>{calendarEvents.length}</div>
                <div className="text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">Scheduled</div>
              </div>
            </div>
            
            <div className="relative overflow-hidden rounded-xl bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200/50 dark:border-slate-600/30 px-4 py-3 shadow-sm">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 to-indigo-50/50 dark:from-purple-900/10 dark:to-indigo-900/10"></div>
              <div className="relative text-center">
                <div className="text-2xl font-bold" style={{ color: '#4a298a' }}>
                  {calendarEvents.filter(e => moment(e.start).isSame(moment(), 'day')).length}
                </div>
                <div className="text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">Today</div>
              </div>
            </div>

            {/* Queue Status - Only show if not locked */}
            {!isScheduleFeatureLocked && (
              <div className={`relative overflow-hidden rounded-xl bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border px-4 py-3 shadow-sm ${
                !queueLimit.canSchedule 
                  ? 'border-red-300 dark:border-red-600' 
                  : (queueLimit.current_count || 0) >= (queueLimit.limit || 0) * 0.8
                    ? 'border-yellow-300 dark:border-yellow-600'
                    : 'border-slate-200/50 dark:border-slate-600/30'
              }`}>
                <div className={`absolute inset-0 ${
                  !queueLimit.canSchedule 
                    ? 'bg-gradient-to-br from-red-50/50 to-red-100/50 dark:from-red-900/10 dark:to-red-800/10'
                    : (queueLimit.current_count || 0) >= (queueLimit.limit || 0) * 0.8
                      ? 'bg-gradient-to-br from-yellow-50/50 to-yellow-100/50 dark:from-yellow-900/10 dark:to-yellow-800/10'
                      : 'bg-gradient-to-br from-green-50/50 to-emerald-50/50 dark:from-green-900/10 dark:to-emerald-900/10'
                }`}></div>
                <div className="relative text-center">
                  <div className="flex items-center justify-center space-x-1">
                    <div className={`text-2xl font-bold ${
                      !queueLimit.canSchedule 
                        ? 'text-red-600 dark:text-red-400'
                        : (queueLimit.current_count || 0) >= (queueLimit.limit || 0) * 0.8
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-green-600 dark:text-green-400'
                    }`}>
                      {queueLimit.unlimited ? '∞' : `${queueLimit.current_count || 0}/${queueLimit.limit || 0}`}
                    </div>
                    {!queueLimit.canSchedule && <AlertTriangle className="w-4 h-4 text-red-500" />}
                  </div>
                  <div className="text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">Queue</div>
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <Globe className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white" />
            <FloatingLabelSelect
              label="Timezone"
              value={timezone}
              onValueChange={setTimezone}
              className="w-full sm:w-auto text-white px-8 sm:px-10 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-indigo-500/25 pl-8 sm:pl-10"
              style={{ background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 100%)' }}
            >
              {timezones.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
            </FloatingLabelSelect>
          </div>
          
          <Button 
            onClick={() => isScheduleFeatureLocked ? setShowUpgradeModal(true) : setIsPostSelectorOpen(true)}
            className="w-full sm:w-auto text-white px-6 sm:px-8 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-indigo-500/25"
            style={{ background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 100%)' }}
          >
            {isScheduleFeatureLocked && <Crown className="h-4 w-4 mr-2 text-yellow-300" />}
            {!isScheduleFeatureLocked && <Clock className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />}
            <span className="hidden sm:inline">Schedule Post</span>
            <span className="sm:hidden">Schedule</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 lg:gap-8">
        {/* Calendar Section */}
        <div className="xl:col-span-2 order-2 xl:order-1">
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 rounded-2xl sm:rounded-3xl blur-xl"></div>
            <div className="relative bg-white dark:bg-slate-800/30 backdrop-blur-2xl rounded-2xl sm:rounded-3xl border border-slate-200/50 dark:border-slate-600/20 shadow-2xl overflow-hidden">

              
              {/* Calendar Body */}
              <div className="p-3 sm:p-4 lg:p-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-100/50 via-white to-slate-100/50 dark:from-slate-900/60 dark:via-slate-800/40 dark:to-slate-900/60 rounded-xl sm:rounded-2xl"></div>
                  <div className="relative bg-white/50 dark:bg-slate-900/20 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-slate-200/50 dark:border-slate-700/30 overflow-hidden">
                    {isLoadingPosts ? (
                      // Calendar Skeleton Loader
                      <div className="p-4" style={{ height: window.innerWidth < 640 ? 400 : window.innerWidth < 1024 ? 500 : 700 }}>
                        <div className="flex items-center justify-between mb-4">
                          <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-32 animate-pulse"></div>
                          <div className="flex space-x-2">
                            <div className="h-8 w-8 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"></div>
                            <div className="h-8 w-8 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"></div>
                          </div>
                        </div>
                        <div className="grid grid-cols-7 gap-2 mb-4">
                          {Array.from({ length: 7 }).map((_, i) => (
                            <div key={i} className="h-6 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"></div>
                          ))}
                        </div>
                        <div className="grid grid-cols-7 gap-2">
                          {Array.from({ length: 35 }).map((_, i) => (
                            <div key={i} className="h-20 bg-slate-100 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 animate-pulse">
                              <div className="p-2">
                                <div className="h-4 w-6 bg-slate-200 dark:bg-slate-700 rounded mb-2 animate-pulse"></div>
                                {Math.random() > 0.7 && (
                                  <div className="h-3 bg-slate-300 dark:bg-slate-600 rounded animate-pulse"></div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <DraggableCalendar
                        localizer={localizer}
                        events={calendarEvents}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ 
                          height: window.innerWidth < 640 ? 400 : window.innerWidth < 1024 ? 500 : 700 
                        }}
                        views={[Views.MONTH, Views.WEEK, Views.DAY]}
                        defaultView={window.innerWidth < 640 ? Views.DAY : Views.MONTH}
                        onEventDrop={handleEventDrop}
                        onSelectEvent={handleSelectEvent}
                        components={{
                          event: EventComponent,
                          toolbar: CustomCalendarToolbar,
                        }}
                        onDropFromOutside={({ start }) => {
                          if (draggedPost) {
                            const updatedData: ScheduleUpdate = {
                              scheduled_at: moment(start).utc().toISOString(),
                              status: 'scheduled',
                            };
                            updateScheduleMutation.mutate({ postId: draggedPost.id, data: updatedData });
                          }
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                        }}
                        resizable
                        selectable
                        popup
                        popupOffset={30}
                      />
                    )}
                  </div>
                </div>
                
                {/* Calendar Legend */}
                <div className="mt-4 sm:mt-6 flex flex-wrap items-center justify-center gap-4 sm:gap-8">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full"></div>
                    <span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Scheduled Posts</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-gradient-to-r from-pink-500 to-orange-500 rounded-full"></div>
                    <span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Selected</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"></div>
                    <span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Today</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="xl:col-span-1 order-1 xl:order-2 space-y-4 sm:space-y-6">
          <ContentQueue />
          <BestTimeToPost />
        </div>
      </div>

      <PostSelectorModal 
        isOpen={isPostSelectorOpen} 
        onClose={() => setIsPostSelectorOpen(false)} 
        onPostSelect={handlePostSelect} 
      />
      {selectedPost && (
        <SchedulePostModal
          isOpen={isScheduleModalOpen}
          onClose={() => setIsScheduleModalOpen(false)}
          post={selectedPost}
        />
      )}
      {selectedPost && (
        <PostPreviewModal
          isOpen={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          post={selectedPost}
          timezone={timezone}
          onPostUnscheduled={handlePostUnscheduled}
        />
      )}
      
      {/* Upgrade Modal for locked features */}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        title="Upgrade to Schedule Posts"
        message="Post scheduling is available on paid plans. Upgrade now to unlock this feature and plan your content in advance."
        feature="scheduling"
      />
      </div>
    </ComingSoonWrapper>
  );
};

export default SchedulePage;